import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import Point from 'ol/geom/Point';
import FeatureFormat from 'ol/format/Feature';

import { SketchObjectType } from 'cadenza/map/classic-sketch-layer/sketch-object-type';
import { SketchFeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import { groupFeaturesByGroup } from 'cadenza/map/classic-sketch-layer/sketch-sidebar/sketch';
import { getMainStyle } from 'cadenza/map/classic-sketch-layer/sketch-style';

import { ImageRectangle } from '../geom/imagerectangle';
import { Line } from '../geom/line';
import { Oval } from '../geom/oval';
import { Rectangle } from '../geom/rectangle';

import { modelStyleToOpenLayersStyle } from './sketch-style';
import { createSketchStrategy as sketchItemStrategy } from './strategy';

const createGeometry = sketchItemStrategy({
  [SketchObjectType.IMAGE]: item => new ImageRectangle(item.from, item.to),
  [SketchObjectType.LINE]: item => new Line([ item.from, item.to ]),
  [SketchObjectType.OVAL]: item => new Oval(item.position, [ item.radius[0], item.radius[1] ]),
  [SketchObjectType.POINT]: item => new Point(item.position),
  [SketchObjectType.POLYGON]: item => new Polygon([ [ ...item.points, item.points[0] ] ]),
  [SketchObjectType.POLYLINE]: item => new LineString(item.points),
  [SketchObjectType.RECTANGLE]: item => new Rectangle(item.from, item.to),
  [SketchObjectType.TEXT]: item => new Point(item.position)
});

const createItemGeometry = sketchItemStrategy({
  [SketchObjectType.LINE]: (item, geometry) => fromTo(geometry.getCoordinates()),
  [SketchObjectType.OVAL]: (item, geometry) => ({ position: geometry.getCenter(), radius: geometry.getCalculatedRadius() }),
  [SketchObjectType.POINT]: (item, geometry) => ({ position: geometry.getCoordinates() }), // point and image point
  [SketchObjectType.TEXT]: (item, geometry) => ({ position: geometry.getCoordinates() }),
  [SketchObjectType.POLYGON]: (item, geometry) => {
    const coordinates = geometry.getCoordinates()[0];
    coordinates.pop(); // The backend does not expect the last coordinate (duplicate of the first).
    return { points: coordinates };
  },
  [SketchObjectType.POLYLINE]: (item, geometry) => ({ points: geometry.getCoordinates() }),
  [SketchObjectType.RECTANGLE]: (item, geometry) => fromTo(geometry.getCorners()),
  [SketchObjectType.IMAGE]: (item, geometry) => fromTo(geometry.getCorners())
});

function createFeature (item) {
  const feature = new Feature({
    geometry: createGeometry(item)
  });
  feature.set(SketchFeatureProperty.STYLE_ITEM, item);
  feature.setStyle(modelStyleToOpenLayersStyle(item));
  feature.setId(item.id);
  if (item.group) {
    feature.set(SketchFeatureProperty.GROUP, item.group);
  }
  return feature;
}

function createItem (feature) {
  const styleItem = feature.get(SketchFeatureProperty.STYLE_ITEM);
  const itemGeometry = createItemGeometry(styleItem, feature.getGeometry());
  const paletteAttributes = getPaletteItemAttributes(feature);
  const extraProperties = getExtraProperties(feature);

  return Object.assign({
    id: feature.getId(),
    style: styleItem.style,
    type: styleItem.type
  }, itemGeometry, paletteAttributes, extraProperties);
}

function getPaletteItemAttributes (feature) {
  const paletteItemReference = feature.get(SketchFeatureProperty.PALETTE_ITEM_REFERENCE);
  return paletteItemReference ? { paletteId: paletteItemReference.paletteId, usedSketchPaletteItemId: paletteItemReference.paletteItemId } : {};
}

function getExtraProperties (feature) {
  const result = {};
  const style = getMainStyle(feature);
  const textStyle = style.getText();
  if (textStyle) {
    result.lines = textStyle.getText().split('\n');
  }
  const group = feature.get(SketchFeatureProperty.GROUP);
  if (group) {
    result.group = group;
  }
  return result;
}

function fromTo (array) {
  return { from: array[0], to: array[1] };
}

export class SketchFormat extends FeatureFormat {

  readFeatures (items) {
    const features = items.map(createFeature);
    const { groups } = groupFeaturesByGroup(features);
    for (const groupFeatures of Object.values(groups)) {
      if (!groupFeatures.length) {
        continue;
      }
      groupFeatures[0].set(SketchFeatureProperty.GROUP_FEATURES, groupFeatures);
    }
    return features;
  }

  writeFeature (feature) {
    return createItem(feature);
  }

}
